/**
 * Account System Types (Schema V3)
 *
 * Scalable multi-role account system with relationship modeling.
 * Supports: learner, parent, teacher accounts with flexible linking.
 *
 * Moved from src/types/accounts.ts in R30 (DDD Fase 3 — types split).
 */

export type AccountRole = 'learner' | 'parent' | 'teacher' | 'admin';

/**
 * Onboarding Step State (UXA-18)
 * Granular tracking of which onboarding steps were completed vs skipped.
 */
export interface OnboardingStepState {
  welcome: 'completed' | 'skipped' | null;
  action: 'completed' | 'skipped' | null;
  followUp: 'completed' | 'skipped' | null;
}

/**
 * Base Account Interface
 * Common fields for all account types.
 */
interface BaseAccount {
  id: string; // Stable UUID
  role: AccountRole;
  displayName: string;
  username?: string; // For login (required for learner)
  email?: string;
  createdAt: number;
  lastActive: number;
  isDemo?: boolean; // Marks demo accounts
  passwordHash?: string; // SHA-256 hash (local, symbolic)
  hasCompletedOnboarding?: boolean; // Fase 2: set true after post-registration onboarding
  onboardingSteps?: OnboardingStepState; // Granular tracking (UXA-18)
}

/**
 * Learner Account
 * Student profile with learning data.
 */
export interface LearnerAccount extends BaseAccount {
  role: 'learner';
  username: string; // Required for learner login
  grade?: number;
  birthYear?: number;
  isPlusUser?: boolean;
  linkCode?: string; // Link code from teacher (optional)
}

/**
 * Parent Account
 * Can view/manage multiple learner accounts.
 */
export interface ParentAccount extends BaseAccount {
  role: 'parent';
  email?: string;
  phoneNumber?: string;
}

/**
 * Classroom
 * Represents a group of students managed by a teacher.
 *
 * Wave 3 (co-teacher refactor, mei 2026): `coTeacherIds` allows multiple
 * teachers to share access to one classroom. `teacherId` remains the single
 * owner (full CRUD rights). Co-teachers can manage students and create
 * homework/assessments but cannot delete or rename the classroom.
 */
export interface Classroom {
  id: string;
  name: string; // 'Groep 6a', 'Groep 7B', 'De Dolfijnen'
  groupLevel: number; // 6, 7, or 8 — determines content difficulty
  learnerIds: string[]; // Student IDs in this classroom
  createdAt: number;
  teacherId: string; // Owner teacher ID (denormalized for quick lookup)
  joinCode: string; // 6-char alphanumeric code for students to join
  /**
   * Aparte code om iemand als CO-DOCENT toe te laten. 8 tekens.
   *
   * ─── WAAROM DIT EEN EIGEN VELD IS (P0, SHORT_CODE_SECURITY.md B-1) ───
   *
   * Hiervoor deelde `InviteCoTeacherPanel` de `joinCode` uit om een collega
   * co-docent te maken. Diezelfde code staat op het bord voor dertig kinderen.
   * Elk kind kon hem dus doorgeven aan iemand die daarmee inzage kreeg in ALLE
   * leerlingen van de klas — rechtenescalatie, geen zwak geheim.
   *
   * De lengte verschilt bewust van de joinCode (8 vs 6): daarmee kan een
   * leerlingcode nooit per ongeluk als docentcode gelden, ook niet als de
   * verzilvering ooit op het verkeerde veld gaat zoeken. Dat is structurele
   * scheiding in plaats van een controle die iemand kan vergeten.
   *
   * Optioneel omdat klassen van vóór deze wijziging het veld niet hebben; de
   * eigenaar maakt er dan één aan op het moment dat hij wil uitnodigen. Een
   * ontbrekende code betekent "niemand kan co-docent worden", en dat is de
   * juiste faalstand.
   *
   * Dit is de LOKALE variant. Zodra de klassen serverzijdig staan, hoort dit
   * een e-mailgebonden, eenmalige uitnodiging te zijn (`coteacher_invites` +
   * `accept_coteacher_invite()`, contract C-8).
   */
  coTeacherCode?: string;
  /**
   * Optional list of co-teacher account IDs. Co-teachers have student-mgmt
   * + homework/toets rights, but not classroom-CRUD or invite/transfer.
   * Defaults to empty array via migration.normalizeV4State.
   */
  coTeacherIds?: string[];
}

/**
 * Teacher Account
 * Can view multiple learner accounts (classroom management).
 */
export interface TeacherAccount extends BaseAccount {
  role: 'teacher';
  email?: string;
  schoolName?: string;
  /**
   * Spiegel van `profiles.teacher_status` (migratie 0020).
   *
   * OPTIONEEL, en dat is de kern: een account dat alleen op dit apparaat
   * bestaat heeft geen serverstatus, en `undefined` betekent hier "onbekend",
   * niet "geweigerd". `permissions.ts` blokkeert alleen bij een expliciete
   * `pending` of `rejected`.
   *
   * Dit veld is GEEN beveiligingsgrens. De echte poort is `is_teacher_of()` in
   * de database; dit veld bestaat zodat de UI niet iets aanbiedt wat de server
   * daarna weigert.
   */
  teacherStatus?: 'pending' | 'verified' | 'rejected';
}

/**
 * Admin Account
 * Full access to governance, developer tools, and account management.
 */
export interface AdminAccount extends BaseAccount {
  role: 'admin';
  email?: string;
}

/**
 * Account Union Type
 */
export type Account = LearnerAccount | ParentAccount | TeacherAccount | AdminAccount;
export interface ArchivedLink {
  learnerId: string;
  archivedAt: number; // ms timestamp
  reason: 'unlink' | 'classroom_removal' | 'classroom_delete';
  fromClassroomIds: string[]; // for restore
}

/**
 * Relationship Graph
 * Bidirectional mappings for account relationships.
 * Designed for efficient lookups in both directions.
 */
export interface RelationshipGraph {
  // Parent → Learners (1:N)
  parentToLearners: Record<string, string[]>; // parentId → learnerIds[]

  // Learner → Parents (N:N - multiple parents can link to same child)
  learnerToParents: Record<string, string[]>; // learnerId → parentIds[]

  // Teacher → Learners (1:N - classroom)
  teacherToLearners: Record<string, string[]>; // teacherId → learnerIds[]

  // Learner → Teachers (N:N - multi-classroom scenarios)
  learnerToTeachers: Record<string, string[]>; // learnerId → teacherIds[]

  // Learner → Classrooms (N:N)
  learnerToClassrooms: Record<string, string[]>; // learnerId → classroomIds[]

  /**
   * Soft-delete archive of teacher↔learner links. Each entry records
   * when and how a learner was unlinked, so a teacher can restore the
   * link within the retention window (30 days).
   */
  archivedLearnerLinks?: Record<string, ArchivedLink[]>; // teacherId → ArchivedLink[]
}

/**
 * Accounts State (replaces ProfilesState)
 * Top-level container for all accounts and relationships.
 */
export interface AccountsState {
  accountsById: Record<string, Account>;
  activeAccountId: string; // Current active account (any role)
  relationships: RelationshipGraph;
  // Top-level classroom registry — canonical source of truth
  classrooms: Record<string, Classroom>;
}

/**
 * Active Context
 * Tracks which account is active and what they're viewing.
 */
export interface ActiveContext {
  accountId: string;
  accountRole: AccountRole;

  // For parent/teacher: which learner are they currently viewing?
  viewingLearnerId?: string;
}

/**
 * Helper type guards
 */
export function isLearnerAccount(account: Account): account is LearnerAccount {
  return account.role === 'learner';
}
export function isParentAccount(account: Account): account is ParentAccount {
  return account.role === 'parent';
}
export function isTeacherAccount(account: Account): account is TeacherAccount {
  return account.role === 'teacher';
}
export function isAdminAccount(account: Account): account is AdminAccount {
  return account.role === 'admin';
}